"use client";

import log from "logbeacon";
import { useQuery } from "@tanstack/react-query";
import { useSnapshot } from "valtio";
import { PositionBroker } from "@/src/features/api/memberApi";
import { QUERY_KEYS } from "@/src/config";
import { rootStore } from "@/src/store";
import { useOspApiClientProvider } from "@/src/hooks/useOspApiClientProvider";

interface UseGetPositionBrokersOptions {
  /** Whether to enable query */
  enabled?: boolean;
  /** Cache time (milliseconds) */
  staleTime?: number;
}

/**
 * Hook for fetching position brokers list
 *
 * @param options Query options
 * @returns Query results and status
 */
export const useGetPositionBrokers = (
  options?: UseGetPositionBrokersOptions,
) => {
  const { memberApi } = useOspApiClientProvider();
  const { enabled = true, staleTime = 10 * 60 * 1000 } = options || {};
  const { isLoggedIn } = useSnapshot(rootStore.login);
  const { ownerInfo } = useSnapshot(rootStore.user);
  const userId = ownerInfo?.id;

  const { data, isLoading, isPending, isFetching, error, refetch } = useQuery<
    PositionBroker[]
  >({
    queryKey: [QUERY_KEYS.POSITION_BROKERS, userId],
    enabled: !!memberApi && isLoggedIn && !!userId && enabled,
    staleTime,
    queryFn: async () => {
      log.debug("[useGetPositionBrokers] queryFn params: none");

      if (!memberApi) {
        throw new Error("memberApi is not available");
      }

      const res = await memberApi.positions
        .getPositionBrokers()
        .catch((error) => {
          log.error("[useGetPositionBrokers] API error:", error);
          return Promise.reject(error);
        });

      if (res?.error) {
        log.error("[useGetPositionBrokers] API response error:", res.error);
        return Promise.reject(res.error);
      }

      log.debug("[useGetPositionBrokers] API response:", res?.data);
      // Extract PositionBroker[] from PositionBrokersResponse
      const brokers: PositionBroker[] | undefined = res?.data?.data;
      return brokers || [];
    },
  });

  return {
    data: data || [],
    isLoading,
    isPending,
    isFetching,
    error,
    refetch,
  };
};
